import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Copies `text`; false when the browser refuses (no permission, insecure context). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * "Copier le lien" (CEO-4, CEO-14): a "Lien copié" toast when it works, and
 * when the clipboard is refused, the URL shown selected in a field so it can
 * still be copied by hand.
 */
export function CopyLink({ url, label }: { url: string; label?: string }) {
  const { t } = useTranslation(['common']);
  const [fallback, setFallback] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Select once the field is on screen (it appears with the state change).
  const [selectTick, setSelectTick] = useState(0);
  useEffect(() => {
    if (selectTick) inputRef.current?.select();
  }, [selectTick]);
  const onCopy = async () => {
    if (await copyText(url)) {
      setFallback(false);
      toast.success(t('common:copyLink.copied'));
    } else {
      setFallback(true);
      setSelectTick((n) => n + 1);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" variant="outline" size="sm" onClick={() => void onCopy()}>
        <Copy className="mr-2 h-4 w-4" aria-hidden />
        {label ?? t('common:copyLink.copy')}
      </Button>
      {fallback && (
        <label className="space-y-1 text-xs">
          <span>{t('common:copyLink.manual')}</span>
          <Input ref={inputRef} readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label={t('common:copyLink.manual')} />
        </label>
      )}
    </div>
  );
}
